<template>
  <div class="questionnaire-detail questionnaire-theme">
    <!-- 无 formId 时提示 -->
    <van-empty v-if="!formId" description="请提供问卷标识 (formId)" class="empty-formid" />

    <div v-else class="detail-layout">
      <!-- 顶部：logo 占位 + 标题 -->
      <header class="detail-header">
        <img
          class="header-logo"
          :src="questionnaireHeaderIconUrl"
          alt=""
        />
        <h1 class="title">{{ form.title || '加载中...' }}</h1>
      </header>

      <!-- Tab 导航 -->
      <van-tabs v-model="activeTab" swipeable class="detail-tabs">
        <van-tab :title="tabInstructions" name="instructions">
          <div class="tab-content" :class="{ 'no-submit-btn': !isSubmissionOpen }">
            <van-loading v-if="loading && !form.id" class="loading-center" />
            <div v-else class="instructions-text">
              {{ instructionsText || '暂无填写须知' }}
            </div>
          </div>
        </van-tab>
        <van-tab :title="tabMyList" name="myList">
          <div class="tab-content" :class="{ 'no-submit-btn': !isSubmissionOpen }">
            <van-pull-refresh v-model="submissionsLoading" @refresh="fetchSubmissions">
              <van-loading v-if="submissionsLoading && submissions.length === 0" class="loading-center" />
              <van-empty v-else-if="!submissionsLoading && submissions.length === 0" description="暂无提交记录" />
              <div v-else class="submission-list">
                <div
                  v-for="item in submissions"
                  :key="item.id"
                  class="submission-card"
                  @click="onSubmissionClick(item)"
                >
                  <div class="card-main">
                    <div class="card-title">{{ getSubmissionTitle(item) }}</div>
                    <div class="card-meta">
                      {{ getCardMetaLabel(item) }}: {{ getCardMetaTime(item) ? formatTime(getCardMetaTime(item)) : '-' }}
                    </div>
                    <div v-if="hasFeedbackDisplay(item)" class="card-feedback">
                      {{ getFeedbackDisplay(item) }}
                    </div>
                  </div>
                  <span :class="['status-tag', getStatusTagClass(item)]">
                    {{ getStatusTagText(item) }}
                  </span>
                </div>
              </div>
            </van-pull-refresh>
          </div>
        </van-tab>
      </van-tabs>

      <!-- 底部固定按钮：仅当允许提交时展示 -->
      <div v-if="isSubmissionOpen" class="footer-actions">
        <van-button
          block
          type="primary"
          round
          class="submit-btn"
          @click="onSubmitClick"
        >
          {{ submitButtonText }}
        </van-button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Tabs, Tab, Loading, Empty, PullRefresh, Button, Toast } from 'vant'
Vue.use(Tabs).use(Tab).use(Loading).use(Empty).use(PullRefresh).use(Button).use(Toast)

import { setTitle } from '@/libs/ds-bridge'
import { ConstConfig } from '@/constants'
import { getFormDetail, getMySubmissions } from '@/api/questionnaire'

export default {
  name: 'QuestionnaireDetail',
  data() {
    return {
      formId: '',
      form: {},
      loading: false,
      activeTab: 'instructions',
      submissions: [],
      submissionsLoading: false
    }
  },
  computed: {
    questionnaireHeaderIconUrl() {
      return `${ConstConfig.qiniuCdn}/questionnaire-icon.png`
    },
    instructionsText() {
      const config = this.form.formConfig
      return (config && config.instructions) ? config.instructions : ''
    },
    tabInstructions() {
      return '填写须知'
    },
    formTypeLabel() {
      return this.form.type || '问卷'
    },
    tabMyList() {
      return `我的${this.formTypeLabel}`
    },
    submitButtonText() {
      return `提交${this.formTypeLabel}`
    },
    isSubmissionOpen() {
      return this.form.isSubmissionOpen === 'OPEN'
    }
  },
  watch: {
    '$route.query.formId': {
      handler(id) {
        if (id) {
          this.formId = id
          this.loadForm()
          this.fetchSubmissions()
        }
      },
      immediate: true
    },
    '$route.query.tab': {
      handler(tab) {
        if (tab === 'myList') {
          this.activeTab = 'myList'
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.formId = this.$route.query.formId || ''
    if (this.formId) {
      this.loadForm()
      this.fetchSubmissions()
    }
  },
  methods: {
    loadForm() {
      if (!this.formId) return
      this.loading = true
      getFormDetail(this.formId)
        .then(res => {
          if (!res) return
          const data = res.data !== undefined ? res.data : res
          this.form = data || {}
          const st = (this.form.subTitle && String(this.form.subTitle).trim())
            ? String(this.form.subTitle).trim()
            : ''
          const embedded = st || '表单详情'
          document.title = embedded
          setTitle(embedded)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchSubmissions() {
      if (!this.formId) return
      this.submissionsLoading = true
      getMySubmissions(this.formId)
        .then(res => {
          if (!res) return
          const raw = res.data !== undefined ? res.data : res
          let list = []
          if (Array.isArray(raw)) {
            list = raw
          } else if (raw && raw.list) {
            list = raw.list
          }
          this.submissions = list
        })
        .finally(() => {
          this.submissionsLoading = false
        })
    },
    getCardMetaLabel(item) {
      if (item.submitStatus === 'DRAFT') return '保存时间'
      const fb = item.latestFeedback
      const hasFeedback = fb && (fb.status || fb.statusName || fb.comment || fb.feedbackAt)
      return hasFeedback ? '反馈时间' : '提交时间'
    },
    getCardMetaTime(item) {
      if (item.submitStatus === 'DRAFT') return item.updatedAt
      const fb = item.latestFeedback
      const hasFeedback = fb && (fb.status || fb.statusName || fb.comment || fb.feedbackAt)
      if (hasFeedback && fb.feedbackAt) return fb.feedbackAt
      return item.submittedAt
    },
    getSubmissionTitle(item) {
      if (item.contentSummary) return item.contentSummary
      if (item.content && typeof item.content === 'object') {
        const first = Object.values(item.content)[0]
        if (typeof first === 'string') return first
      }
      return `申请记录 #${item.id || ''}`
    },
    hasFeedbackDisplay(item) {
      const fb = item.latestFeedback
      return fb && (fb.statusName || fb.comment)
    },
    getFeedbackDisplay(item) {
      const fb = item.latestFeedback
      if (!fb) return ''
      const name = fb.statusName || fb.status || ''
      const comment = fb.comment || ''
      return comment ? `${name}:${comment}` : name
    },
    getStatusTagText(item) {
      if (item.submitStatus !== 'SUBMITTED') return '草稿'
      const fb = item.latestFeedback
      const hasFeedback = fb && (fb.status || fb.statusName || fb.comment || fb.feedbackAt)
      return hasFeedback ? '已回复' : '已提交'
    },
    getStatusTagClass(item) {
      if (item.submitStatus !== 'SUBMITTED') return 'draft'
      const fb = item.latestFeedback
      const hasFeedback = fb && (fb.status || fb.statusName || fb.comment || fb.feedbackAt)
      return hasFeedback ? 'replied' : 'pending'
    },
    formatTime(val) {
      if (!val) return ''
      const d = typeof val === 'number' ? new Date(val) : new Date(String(val))
      if (Number.isNaN(d.getTime())) return String(val)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${y}-${m}-${day} ${h}:${min}`
    },
    onSubmitClick() {
      this.$router.push({
        path: '/questionnaire/form',
        query: { formId: this.formId }
      })
    },
    onSubmissionClick(item) {
      if (!this.formId || !item || !item.id) return
      const subId = String(item.id)
      if (item.submitStatus === 'DRAFT') {
        this.$router.push({
          path: '/questionnaire/form',
          query: { formId: this.formId, submissionId: subId }
        })
      } else {
        this.$router.push({
          path: '/questionnaire/submission-detail',
          query: { formId: this.formId, submissionId: subId }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-detail {
  min-height: 100vh;
  background: #f5f5f5;

  .empty-formid {
    padding-top: 80px;
  }
}

/* 固定 header + tab，仅内容区滚动 */
.detail-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.detail-header {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  padding: 16px;
  background: var(--qn-theme);
  color: #fff;

  .header-logo {
    width: 48px;
    height: 48px;
    min-width: 48px;
    border-radius: 8px;
    object-fit: contain;
    background: rgba(255, 255, 255, 0.2);
    display: block;
  }

  .title {
    margin: 0 0 0 12px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    flex: 1;
  }
}

/* 让 tab 内容区成为唯一滚动区域 */
.detail-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

::v-deep .detail-tabs .van-tabs__wrap {
  flex-shrink: 0;
  background: #fff;
}

::v-deep .detail-tabs .van-tabs__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

::v-deep .detail-tabs .van-tabs__line {
  background-color: var(--qn-theme);
}

::v-deep .detail-tabs .van-tab--active {
  color: #333;
}

::v-deep .detail-tabs .van-tab {
  color: #999;
}

.tab-content {
  min-height: 200px;
  padding: 16px;
  padding-bottom: 70px; /* 为底部固定按钮留空 */
  background: #f5f5f5;

  &.no-submit-btn {
    padding-bottom: 16px;
  }

  .loading-center {
    display: flex;
    justify-content: center;
    padding: 40px 0;
  }

  .instructions-text {
    white-space: pre-wrap;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
    background: #fff;
    padding: 16px;
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .submission-list {
    .submission-card {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 14px 16px;
      margin-bottom: 12px;
      background: #fff;
      border-radius: 4px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
      cursor: pointer;

      .card-main {
        flex: 1;
        min-width: 0;

        .card-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
        }

        .card-meta {
          font-size: 12px;
          color: #666;
          line-height: 1.5;
        }

        .card-feedback {
          margin-top: 6px;
          font-size: 12px;
          color: #666;
          line-height: 1.5;
        }
      }

      .status-tag {
        flex-shrink: 0;
        margin-left: 8px;
        padding: 3px 10px;
        border-radius: 2px;
        font-size: 12px;

        &.draft {
          background: #f5f5f5;
          color: #999999;
        }

        &.pending {
          background: #fff7e6;
          color: #ff8d24;
        }

        &.replied {
          background: #e6f7ff;
          color: #1890ff;
        }
      }
    }
  }
}

.footer-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);

  .submit-btn {
    height: 44px;
    font-size: 16px;
    background: var(--qn-theme);
    border: none;
  }
}
</style>
